import React, { useEffect, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Navigation, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { getGeo, estimateFare, createRide, getMyRides, acceptBid, advanceState, formatApiError } from "../lib/api";
import { naira, StateBadge } from "./appHelpers";
import { PassengerRideCard } from "./PassengerRideCard";

export default function PassengerApp() {
  const [geo, setGeo] = useState({ cities: [], categories: [] });
  const [form, setForm] = useState({ city: "Lagos", category: "Instant Comfort", pickup: "", destination: "" });
  const [estimate, setEstimate] = useState(null);
  const [proposed, setProposed] = useState("");
  const [loading, setLoading] = useState(false);
  const [rides, setRides] = useState([]);

  const cityObj = geo.cities.find((c) => c.name === form.city);
  const locations = cityObj ? cityObj.locations : [];

  const loadRides = useCallback(async () => {
    try { setRides(await getMyRides()); } catch (e) { console.error("Failed to load rides:", e); }
  }, []);

  useEffect(() => { getGeo().then(setGeo).catch((e) => console.error("Failed to load geo:", e)); }, []);
  useEffect(() => { loadRides(); const t = setInterval(loadRides, 5000); return () => clearInterval(t); }, [loadRides]);

  const doEstimate = async () => {
    if (!form.pickup || !form.destination) return toast.error("Choose pickup and destination.");
    if (form.pickup === form.destination) return toast.error("Pickup and destination must differ.");
    setLoading(true);
    try {
      const est = await estimateFare(form);
      setEstimate(est);
      setProposed(String(est.estimate));
    } catch (e) { toast.error("Could not estimate fare."); } finally { setLoading(false); }
  };

  const book = async () => {
    if (!estimate) return doEstimate();
    const amt = parseInt(proposed, 10);
    if (!amt || amt < estimate.min_acceptable) return toast.error(`Offer at least ${naira(estimate.min_acceptable)}.`);
    setLoading(true);
    try {
      await createRide({ ...form, proposed_fare: amt });
      toast.success("Ride requested! Waiting for driver bids.");
      setEstimate(null); setProposed("");
      loadRides();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setLoading(false); }
  };

  const activeRides = rides.filter((r) => !["TRIP_COMPLETED", "PASSENGER_CANCELLED", "DRIVER_CANCELLED", "NO_SHOW", "SYSTEM_CANCELLED"].includes(r.state));
  const pastRides = rides.filter((r) => !activeRides.includes(r));

  const onAccept = async (rideId, bidId) => {
    try { await acceptBid(rideId, bidId); toast.success("Driver confirmed!"); loadRides(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); loadRides(); }
  };
  const cancel = async (id) => {
    try { await advanceState(id, "PASSENGER_CANCELLED"); toast("Ride cancelled."); loadRides(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const inputCls = "h-12 bg-[#0a0e0c] border-white/15 text-white focus-visible:ring-[#2fd645]";

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
      {/* Booking panel */}
      <div className="rounded-3xl border border-white/10 bg-[#0e1311] p-6">
        <h2 className="font-display font-extrabold text-white text-2xl mb-5">Book a ride</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label className="text-white/80">City</Label>
              <Select value={form.city} onValueChange={(v) => { setForm({ ...form, city: v, pickup: "", destination: "" }); setEstimate(null); }}>
                <SelectTrigger data-testid="ride-city-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0e1311] border-white/15 text-white">
                  {geo.cities.map((c) => <SelectItem key={c.name} value={c.name} className="focus:bg-[#2fd645]/15 focus:text-white">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="text-white/80">Ride type</Label>
              <Select value={form.category} onValueChange={(v) => { setForm({ ...form, category: v }); setEstimate(null); }}>
                <SelectTrigger data-testid="ride-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0e1311] border-white/15 text-white">
                  {geo.categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#2fd645]/15 focus:text-white">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2"><Label className="text-white/80">Pickup</Label>
            <Select value={form.pickup} onValueChange={(v) => { setForm({ ...form, pickup: v }); setEstimate(null); }}>
              <SelectTrigger data-testid="ride-pickup-select" className={inputCls}><SelectValue placeholder="Where are you?" /></SelectTrigger>
              <SelectContent className="bg-[#0e1311] border-white/15 text-white">
                {locations.map((l) => <SelectItem key={l} value={l} className="focus:bg-[#2fd645]/15 focus:text-white">{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label className="text-white/80">Destination</Label>
            <Select value={form.destination} onValueChange={(v) => { setForm({ ...form, destination: v }); setEstimate(null); }}>
              <SelectTrigger data-testid="ride-destination-select" className={inputCls}><SelectValue placeholder="Where to?" /></SelectTrigger>
              <SelectContent className="bg-[#0e1311] border-white/15 text-white">
                {locations.map((l) => <SelectItem key={l} value={l} className="focus:bg-[#2fd645]/15 focus:text-white">{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {estimate && (
            <div className="rounded-2xl border border-[#2fd645]/30 bg-[#2fd645]/5 p-4">
              <div className="flex items-center justify-between text-sm text-white/70">
                <span className="flex items-center gap-1"><Navigation className="h-4 w-4 text-[#2fd645]" /> {estimate.distance_km} km</span>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-[#2fd645]" /> ~{estimate.duration_min} min</span>
              </div>
              <div className="mt-2 text-white/60 text-sm">Suggested fare: <b className="text-white">{naira(estimate.estimate)}</b></div>
              <div className="mt-3 space-y-2">
                <Label className="text-white/80">Your offer (you can bid your price)</Label>
                <Input data-testid="ride-offer-input" className={inputCls} inputMode="numeric" value={proposed} onChange={(e) => setProposed(e.target.value.replace(/\D/g, ""))} />
                <p className="text-xs text-white/40">Minimum accepted: {naira(estimate.min_acceptable)}</p>
              </div>
            </div>
          )}

          <Button data-testid="ride-submit-button" onClick={estimate ? book : doEstimate} disabled={loading} className="w-full h-12 bg-[#2fd645] text-[#06210b] font-bold hover:bg-[#46ff5c] rounded-xl disabled:opacity-70">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (estimate ? "Request ride" : "Get fare estimate")}
          </Button>
        </div>
      </div>

      {/* Rides panel */}
      <div className="space-y-4">
        <h2 className="font-display font-extrabold text-white text-2xl">Your rides</h2>
        {activeRides.length === 0 && <p className="text-white/50">No active rides. Book one to get started.</p>}
        {activeRides.map((r) => <PassengerRideCard key={r.id} ride={r} onAccept={onAccept} onCancel={cancel} />)}

        {pastRides.length > 0 && (
          <div className="pt-2">
            <div className="text-sm font-semibold text-white/60 mb-2">History</div>
            {pastRides.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-[#0b0f0d] px-4 py-3 mb-2">
                <div className="text-sm text-white/60">{r.pickup} → {r.destination}</div>
                <StateBadge state={r.state} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
